'use client';

import { useId, useMemo, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useSearchParams, useParams } from 'next/navigation';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { getContactFormSchema, type ContactFormData } from '@/lib/validations/contact';
import CalendlyPopup from '@/components/ui/CalendlyWidget';

type SocialIconType = 'linkedin';

const SOCIAL_ICON_CONFIG: Record<SocialIconType, {
  width: number;
  height: number;
  viewBox: string;
  path: string;
  gradient: { x1: string; y1: string; x2: string; y2: string; };
}> = {
  linkedin: {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
    gradient: { x1: '0', y1: '0', x2: '1', y2: '1' }
  }
};

const GradientSocialIcon = ({ type }: { type: SocialIconType }) => {
  const gradientId = useId();
  const config = SOCIAL_ICON_CONFIG[type];

  return (
    <svg
      width={config.width}
      height={config.height}
      viewBox={config.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1={config.gradient.x1}
          y1={config.gradient.y1}
          x2={config.gradient.x2}
          y2={config.gradient.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#351EE0" />
          <stop offset="1" stopColor="#3F74EF" />
        </linearGradient>
      </defs>
      <path d={config.path} fill={`url(#${gradientId})`} />
    </svg>
  );
};

export default function ContactFormSection() {
  const t = useTranslations('Contact');
  const searchParams = useSearchParams();
  const params = useParams();
  const locale = params.locale as string || 'en';
  
  // Form data type without locale since we'll add it on submit
  type FormInputs = Omit<ContactFormData, 'locale'>;
  
  // Get validation schema with translated error messages
  const validationSchema = useMemo(() => getContactFormSchema(locale).omit({ locale: true }), [locale]);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue
  } = useForm<FormInputs>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur'
  });

  // Pre-fill form from URL parameters
  useEffect(() => {
    const name = searchParams.get('name');
    const email = searchParams.get('email');
    const phone = searchParams.get('phone');
    const isPrefilled = searchParams.get('prefilled');
    
    if (isPrefilled === 'true') {
      // Set form values if they exist
      if (name) setValue('name', name, { shouldValidate: false });
      if (email) setValue('email', email, { shouldValidate: false });
      if (phone) setValue('phone', phone, { shouldValidate: false });
      
      // Check if all fields are empty (form was submitted without filling any fields)
      const areAllFieldsEmpty = !name && !email && !phone;
      
      if (areAllFieldsEmpty) {
        // Show "please fill inputs" message with warning style
        toast.warning(t('questionSection.emptyForm.message'), {
          description: t('questionSection.emptyForm.description'),
          duration: 6000,
          action: {
            label: '✕',
            onClick: () => {},
          },
        });
      } else {
        // Show informative toast message for partially filled form
        toast.info(t('questionSection.prefilled.message'), {
          description: t('questionSection.prefilled.description'),
          duration: 6000,
          action: {
            label: '✓',
            onClick: () => {},
          },
        });
      }
      
      // Scroll to the contact form section
      setTimeout(() => {
        const contactFormElement = document.getElementById('contact-form');
        if (contactFormElement) {
          contactFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [searchParams, setValue, t]);

  const onSubmit = async (data: FormInputs) => {
    try {
      // Add locale to the form data
      const dataWithLocale: ContactFormData = {
        ...data,
        locale: locale,
      };
      
      // Use the new leads API that integrates with CRM
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataWithLocale),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle rate limit error
        if (response.status === 429) {
          toast.error(t('questionSection.rateLimit.title'), {
            description: t('questionSection.rateLimit.description'),
            duration: 8000,
          });
          return;
        }

        throw new Error(result.error || 'Failed to send message');
      }

      // Track successful submission
      (window as any).dataLayer?.push({
        event: "contact_form_submit",
        source: window.location.href
      });

      // Reset form
      reset();

      // Show success message - seller will contact them
      toast.success(t('questionSection.success.title'), {
        description: t('questionSection.success.description'),
        duration: 6000,
      });

    } catch (error) {
      // Error
      toast.error(t('questionSection.error.title'), {
        duration: 4000,
        action: {
          label: '✕',
          onClick: () => {},
        },
      });
      console.error('Contact form error:', error);
    }
  };

  const headingLines = useMemo(
    () =>
      t('questionSection.heading')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    [t]
  );

  const socialLinks: Array<{ id: SocialIconType; href: string; label: string }> = useMemo(
    () => [
      { id: 'linkedin', href: 'https://www.linkedin.com/company/ciscode', label: t('questionSection.social.linkedin') }
    ],
    [t]
  );

  const inputClassName =
    'h-14 rounded-[10px] border border-[#E4E7F2] bg-[#F7F8FC] px-5 text-sm font-medium text-x-900 placeholder:text-[#8C90A4] focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-0';

  const messageClassName =
    'w-full rounded-[12px] border border-[#E4E7F2] bg-[#F7F8FC] px-5 py-4 text-sm font-medium text-x-900 placeholder:text-[#8C90A4] focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary';

  const submitButtonClassName =
    'h-14 w-full rounded-[5px] bg-brand-gradient font-button font-[number:var(--button-font-weight)] text-sm text-white shadow-[0_16px_32px_rgba(53,30,224,0.35)] transition-all duration-200 hover:scale-[1.01] hover:shadow-[0_22px_40px_rgba(53,30,224,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F8FC] sm:text-base disabled:opacity-70 disabled:cursor-not-allowed';

  return (
    <section id="contact-form" className="bg-white py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:items-start">
          {/* Content */}
          <div className="space-y-6 lg:pr-8 rtl:lg:pl-8 rtl:lg:pr-0">
            <h2 className="flex flex-col gap-1 font-heading-h1 font-[number:var(--heading-h1-font-weight)] lg:text-[length:var(--heading-h1-font-size)] leading-[0.95] tracking-[-0.01em]">
              {headingLines.map((line, index) => (
                <span key={`${line}-${index}`} className="block">
                  {line}
                </span>
              ))}
            </h2>
            <p className="max-w-md font-body-m font-[number:var(--body-m-font-weight)] text-x-800 text-base leading-relaxed">
              {t('questionSection.description')}
            </p>
            <div className="flex items-center gap-4 rtl:flex-row-reverse rtl:gap-4">
              {socialLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={link.label}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-[12px] border border-[#E7E9F1] bg-white shadow-[0_10px_24px_rgba(15,35,85,0.08)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(15,35,85,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2"
                >
                  <GradientSocialIcon type={link.id} />
                </a>
              ))}
            </div>
          </div>

          {/* Form */}
          <div>
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Input
                    type="text"
                    placeholder={t('questionSection.placeholders.name')}
                    autoComplete="name"
                    aria-required="true"
                    aria-invalid={errors.name ? 'true' : 'false'}
                    disabled={isSubmitting}
                    className={inputClassName}
                    {...register('name')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600" role="alert">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                
                <div>
                  <Input
                    type="email"
                    placeholder={t('questionSection.placeholders.email')}
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    disabled={isSubmitting}
                    className={inputClassName}
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600" role="alert">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <Input
                    type="tel"
                    placeholder={t('questionSection.placeholders.phone')}
                    autoComplete="tel"
                    inputMode="tel"
                    aria-required="true"
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    disabled={isSubmitting}
                    className={inputClassName}
                    {...register('phone')}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600" role="alert">
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                <div>
                  <Input
                    type="text"
                    placeholder={t('questionSection.placeholders.subject')}
                    autoComplete="off"
                    aria-required="true"
                    aria-invalid={errors.subject ? 'true' : 'false'}
                    disabled={isSubmitting}
                    className={inputClassName}
                    {...register('subject')}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600" role="alert">
                      {errors.subject.message}
                    </p>
                  )}
                </div>
              </div>



              <div>
                <textarea
                  placeholder={t('questionSection.placeholders.message')}
                  aria-required="true"
                  aria-invalid={errors.message ? 'true' : 'false'}
                  disabled={isSubmitting}
                  rows={5}
                  className={messageClassName}
                  {...register('message')}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {errors.message.message}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={submitButtonClassName}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg 
                        className="animate-spin h-5 w-5" 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24"
                      >
                        <circle 
                          className="opacity-25" 
                          cx="12" 
                          cy="12" 
                          r="10" 
                          stroke="currentColor" 
                          strokeWidth="4"
                        />
                        <path 
                          className="opacity-75" 
                          fill="currentColor" 
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {t('questionSection.sending')}
                    </span>
                  ) : (
                    t('questionSection.submitCta')
                  )}
                </Button>

                {/* Divider with text */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">{t('questionSection.orSchedule')}</span>
                  </div>
                </div>

                {/* Calendly Button */}
                <CalendlyPopup
                  buttonText={t('questionSection.scheduleCall')}
                  variant="secondary"
                  className="w-full"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
